import React, {ChangeEvent, ReactNode, useRef} from 'react';
import {Input} from "edozvola-frontend-lib/dist/ui";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {MAX_FILE_SIZE, messages} from "app/config/constants";
import {showToast} from "app/shared/util/toast.util";
import {toast} from "react-toastify";
import {faCircleNotch, faXmark} from "@fortawesome/free-solid-svg-icons";
import {InputGroup, InputGroupText} from "reactstrap";
import {declassify} from "app/shared/util/common";
import {IconProp} from "@fortawesome/fontawesome-svg-core";

export interface FileUploadProps {
  clearButton?: boolean;
  allowedExtensions?: string[];
  setFile: (selectedFile: File) => void;
  className?: string;
  value: string;
  error?: string;
  disabled?: boolean;
  appendIcon?: IconProp;
  onClear?: () => void;
  appendOnClick?: (e) => void;
  previewAttachmentPending?: boolean;
}

const FileUpload = ({
                      clearButton,
                      allowedExtensions,
                      setFile,
                      className,
                      value,
                      error,
                      disabled,
                      appendIcon,
                      appendOnClick,
                      onClear,
                      previewAttachmentPending
}: FileUploadProps) => {

  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  allowedExtensions = allowedExtensions ?? ['pdf'];

  const extensionAllowed = (fileName: string) => {
    const extension = fileName.substring(fileName.lastIndexOf('.') + 1, fileName.length);
    return allowedExtensions.includes(extension.toLowerCase());
  };

  const appendFile = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      return;
    }
    if (selectedFile.size > MAX_FILE_SIZE) {
      showToast(toast.error, messages.FILE_TOO_LARGE);
    } else if (!extensionAllowed(selectedFile.name)) {
      showToast(toast.error, `Datoteka mora biti u ${allowedExtensions.join(' ili ')} formatu!`);
    } else {
      setFile(selectedFile);
    }
  };

  const onClick = (e) => {
    hiddenFileInput.current?.showPicker();
    hiddenFileInput.current?.blur();
    inputRef.current?.blur();
  }

  const onClearClicked = () => {
    if (hiddenFileInput.current) {
      hiddenFileInput.current.files = null;
      hiddenFileInput.current.value = '';
      if (onClear !== undefined) {
        onClear();
      }
      else {
        setFile(null);
      }
    }
  }

  return (
    <InputGroup>
      <Input
        className={declassify({[className]: true, 'hand': !disabled, 'is-invalid': error?.length > 0})}
        onClick={!disabled ? onClick : undefined}
        value={value}
        error={error}
        disabled={disabled}
        innerRef={inputRef}
        addonAppend={
          previewAttachmentPending ?
            <FontAwesomeIcon
              icon={faCircleNotch}
              spin={true}
            /> :
            <FontAwesomeIcon
              className={declassify({ hand: !disabled })}
              icon={appendIcon ?? 'paperclip'}
              onClick={!disabled ? appendOnClick ?? onClick : undefined}
            />
        }
      />
      {clearButton && value?.length > 0 && !disabled && <span
        onClick={onClearClicked}
        className={'x-icon hand'}
      >
        <FontAwesomeIcon icon={faXmark} />
      </span>}
      <input
        type="file"
        ref={hiddenFileInput}
        onInput={appendFile}
        accept={allowedExtensions.map(extension => `.${extension}`).join(',')}
        className="hidden-upload"
      />
    </InputGroup>
  );
}

export default FileUpload;
